'use client'

import { ComponentType, Fragment, useEffect, useState } from 'react'
import { Typography, Grid, Box, Stack, Card, CardHeader, Button, SvgIconProps } from '@mui/material'
import { useTheme, ThemeProvider, createTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'next/navigation'
import Image from 'next/image'

import edayLogo from '@public/images/logos/Gears-eday-logo.png'

import Link from '@/components/common/Link'
import XboxIcon from '@/components/common/icons/XboxIcon'
import SteamIcon from '@/components/common/icons/SteamIcon'
import TCIcon from '@/components/common/icons/TCIcon'
import StatusCheckIcon from '@/components/common/icons//StatusCheck'
import StatusMinusIcon from '@/components/common/icons/StatusMinux'
import StatusExclamationMarkIcon from '@/components/common/icons/StatusExclamationMark'
import StatusQuestionMarkIcon from '@/components/common/icons/StatusQuestionMark'
import { getRegionStatusFromCode, type RegionAvailabilityFeed, type RegionStatus, type StatusData } from '@/types/status'

const SCRATCH_OFFSET = 40

const findBuilds = (builds: RegionAvailabilityFeed['Builds'], changeLists: number[]): RegionAvailabilityFeed['Builds'] | undefined => {
  const matchingBuilds = builds.filter((build) => build.ChangeLists.some((changeList) => changeLists.includes(Number(changeList))))

  return matchingBuilds.length > 0 ? matchingBuilds : undefined
}

const getRegionAvailability = (availabilityFeed: RegionAvailabilityFeed, pinnedChangeLists: number[] = []): Record<string, RegionStatus> => {
  const builds = availabilityFeed.Builds ?? []
  const latestChangeList = Math.max(0, ...builds.flatMap((build) => build.ChangeLists.map((cl) => Number(cl) || 0)))
  const selectedBuilds = findBuilds(builds, pinnedChangeLists) ?? findBuilds(builds, [latestChangeList]) ?? []
  const worst: Record<string, number> = {}

  for (const build of selectedBuilds) {
    // for conflicts, take worst availability status
    for (const { Region, Availability } of build.Regions ?? []) {
      worst[Region] = Math.max(worst[Region] ?? 0, Availability)
    }
  }

  return Object.fromEntries(Object.entries(worst).map(([region, availability]) => [region, getRegionStatusFromCode(availability)]))
}

const formatRegionName = (region: string) =>
  region
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim()

interface StatusMeta {
  Icon: ComponentType<SvgIconProps>
  color: string
  labelKey: string
}

const STATUS_META: Record<string, StatusMeta> = {
  healthy: { Icon: StatusCheckIcon, color: 'success.main', labelKey: 'Healthy' },
  ready: { Icon: StatusCheckIcon, color: 'success.main', labelKey: 'Ready' },
  degraded: { Icon: StatusMinusIcon, color: 'warning.main', labelKey: 'Degraded' },
  critical: { Icon: StatusExclamationMarkIcon, color: 'error.main', labelKey: 'Critical' },
  available: { Icon: StatusCheckIcon, color: 'success.main', labelKey: 'Available' },
  limited: { Icon: StatusMinusIcon, color: 'warning.main', labelKey: 'Limited' },
  unavailable: { Icon: StatusExclamationMarkIcon, color: 'error.main', labelKey: 'Unavailable' },
  unknown: { Icon: StatusQuestionMarkIcon, color: 'text.secondary', labelKey: 'Unknown' }
}

function StatusList({ entries, getLabel, t }: { entries: [string, string][]; getLabel: (key: string) => string; t: (key: string) => string }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr auto', sm: '1fr auto auto' },
        alignItems: 'center',
        columnGap: 2,
        rowGap: { xs: 1, sm: 3 }
      }}>
      {entries.map(([key, value]) => {
        const { Icon, color, labelKey } = STATUS_META[value] ?? STATUS_META.unknown

        return (
          <Fragment key={key}>
            <Typography variant='h3' sx={{ minWidth: 0, overflowWrap: 'anywhere', gridRow: { xs: 'span 2', sm: 'auto' }, alignSelf: 'flex-start' }}>
              {getLabel(key)}
            </Typography>
            <Icon sx={{ color, fontSize: '2rem', justifySelf: 'center' }} />
            <Typography
              sx={{
                color,
                justifySelf: { xs: 'center', sm: 'start' },
                textAlign: { xs: 'center', sm: 'left' },
                mb: { xs: 2, sm: 0 }
              }}>
              {t(labelKey)}
            </Typography>
          </Fragment>
        )
      })}
    </Box>
  )
}

function NetworksSection({ t, centered = false }: { t: (key: string) => string; centered?: boolean }) {
  return (
    <Box>
      <Typography variant='h2' sx={{ mb: 3, textAlign: centered ? 'center' : 'left' }}>
        {t('Networks')}
      </Typography>
      <Stack spacing={3} direction='row' justifyContent={centered ? 'center' : 'flex-start'} sx={{ mt: 2 }}>
        <Stack alignItems='center' spacing={0.5}>
          <XboxIcon
            sx={(theme) => ({
              color: theme.palette.text.primary,
              [theme.accessibility.highContrast.mediaQuery('active')]: { color: 'CanvasText' }
            })}
            fontSize={'xlarge'}
          />
          <Link href='https://support.xbox.com/en-US/xbox-live-status'>{t('Xbox')}</Link>
        </Stack>
        <Stack alignItems='center' spacing={0.5}>
          <SteamIcon
            sx={(theme) => ({
              color: theme.palette.text.primary,
              [theme.accessibility.highContrast.mediaQuery('active')]: { color: 'CanvasText' }
            })}
            fontSize={'xlarge'}
          />
          <Link href='https://store.steampowered.com/charts'>{t('Steam')}</Link>
        </Stack>
      </Stack>
    </Box>
  )
}

export default function StatusHome() {
  const { t, i18n } = useTranslation()
  const theme = useTheme()

  const [liveStatus, setLiveStatus] = useState<StatusData | null>(null)
  const [regionStatus, setRegionStatus] = useState<Record<string, RegionStatus> | null>(null)
  const [liveLastUpdated, setLiveLastUpdated] = useState<string | null>(null)
  const searchParams = useSearchParams()
  const isPreview = searchParams.has('preview')

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      try {
        const res = await fetch(isPreview ? '/status_preview.json' : '/status.json')
        if (!res.ok) return
        const data = (await res.json()) as StatusData
        const lastModified = res.headers.get('last-modified')
        if (cancelled) return
        setLiveStatus(data)
        setLiveLastUpdated(lastModified)

        if (data.regionUrl) {
          try {
            const regionRes = await fetch(data.regionUrl)
            if (!regionRes.ok || cancelled) return
            const feed = (await regionRes.json()) as RegionAvailabilityFeed
            setRegionStatus(getRegionAvailability(feed, data.changeLists))
          } catch {
            // Region feed unavailable, hide the Game Servers section
          }
        }
      } catch {
        // Fall through, show "unknown" status
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [isPreview])

  const services = liveStatus?.services && typeof liveStatus.services === 'object' ? Object.entries(liveStatus.services) : null
  const regions = regionStatus && Object.keys(regionStatus).length > 0 ? Object.entries(regionStatus) : null
  const supportLink = typeof liveStatus?.supportLink === 'string' && liveStatus.supportLink.length > 0 ? liveStatus.supportLink : null

  const lastUpdatedLabel = liveLastUpdated ? new Date(liveLastUpdated).toLocaleString(i18n.language) : '—'

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box
        sx={{
          minHeight: 'max(50vh, 400px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: 'url(/images/backgrounds/Key_art.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'bottom left',
          backgroundRepeat: 'no-repeat'
        }}>
        <Grid container spacing={2} sx={{ alignItems: 'center', width: '80%', mx: 'auto' }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              component={Image}
              src={edayLogo}
              alt={t('Gears of War: E-Day logo')}
              priority
              sizes='(max-width: 600px) 80vw, 600px'
              sx={{
                width: { xs: 'auto', md: '100%' },
                height: { xs: 'auto', md: 'auto' },
                maxWidth: { xs: '100%', md: 600 },
                maxHeight: { xs: 200, md: 'none' }
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant='h1' sx={{ textAlign: 'center' }}>
              {t('Status')}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Box
        sx={{
          position: 'relative',
          top: `-${SCRATCH_OFFSET}px`,
          backgroundImage: 'url(/images/bg-default-scratch.png)',
          backgroundRepeat: 'repeat-x',
          backgroundPosition: 'top left'
        }}>
        <Box sx={{ pt: `${SCRATCH_OFFSET}px`, width: { xs: '90%', md: '80%' }, mx: 'auto' }}>
          <Card variant='outlined' sx={{ mt: 6 }}>
            <CardHeader
              avatar={
                <TCIcon
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    fontSize: { xs: '2.5rem', sm: '4rem' },
                    [theme.accessibility.highContrast.mediaQuery('active')]: { color: 'CanvasText' }
                  })}
                />
              }
              title={
                <Typography variant='h3' component='h2'>
                  {t('Last Updated')}
                </Typography>
              }
              subheader={
                <Typography variant='subtitle1' component='p'>
                  {lastUpdatedLabel}
                </Typography>
              }
              action={
                !services && (
                  <Stack spacing={1} direction='row' alignItems='center' sx={{ pr: 2, height: '100%' }}>
                    <StatusQuestionMarkIcon sx={{ color: STATUS_META.unknown.color, fontSize: '2rem' }} />
                    <Typography sx={{ color: STATUS_META.unknown.color }}>{t(STATUS_META.unknown.labelKey)}</Typography>
                  </Stack>
                )
              }
            />
          </Card>
          <Grid container sx={{ py: 6 }} justifyContent='center' rowSpacing={4} columnSpacing={{ lg: 20, md: 10 }}>
            <Grid size={{ xs: 12, md: 6 }} sx={{ width: '100%' }}>
              <Stack gap={4} sx={{ mb: 3 }}>
                <Typography variant='h2'>{t('Online Services')}</Typography>
                {services && <StatusList entries={services} t={t} getLabel={(key) => t(`services.${key}`, { defaultValue: key })} />}
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }} sx={{ width: '100%' }}>
              {regions ? (
                <Stack gap={4} sx={{ mb: 3 }}>
                  <Typography variant='h2'>{t('Game Servers')}</Typography>
                  <StatusList entries={regions} t={t} getLabel={(key) => t(`regions.${key}`, { defaultValue: formatRegionName(key) })} />
                </Stack>
              ) : (
                <NetworksSection t={t} />
              )}
            </Grid>
          </Grid>
          {regions && <NetworksSection t={t} centered />}
        </Box>
      </Box>
      {supportLink && (
        <ThemeProvider
          theme={createTheme(theme, {
            palette: {
              background: { default: '#ffffff' },
              text: { primary: '#000000' },
              action: { hover: '#cccccc' }
            }
          })}>
          <Box
            sx={{
              py: 6,
              borderTop: `1px solid ${theme.palette.divider}`,
              backgroundColor: 'background.default',
              color: 'text.primary',
              justifyContent: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4
            }}>
            <Typography variant='h3' component='h2' sx={{ textAlign: 'center' }}>
              {t('Experience an issue?')}
            </Typography>
            <Button variant='action' href={supportLink} size='large' sx={{ maxWidth: { xs: '90%', sm: 'auto' } }}>
              {t('Let us know')}
            </Button>
          </Box>
        </ThemeProvider>
      )}
    </Box>
  )
}
